import { useEffect, useMemo, useState } from "react";
import { getLand } from "../../data/request";
import { LandRecord } from "../../types/LandRecord";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TextField,
  CircularProgress,
  Box,
  Typography,
  TablePagination,
} from "@mui/material";
import { getValueDisplay, getUkDateDisplay } from "../../data/utilities";
import { TEXT_SECONDARY } from "../colors";

const ROWS_PER_PAGE = 25;

// "Price Paid" is numeric and "Date Proprietor Added" is "DD-MM-YYYY" - both
// need their own comparison rather than plain string sort, which would put
// "Price Paid" in lexicographic (not numeric) order and get the date column
// completely wrong (sorting by day-of-month before year).
const parseUkDate = (value: string | undefined): number => {
  if (!value) return 0;
  const [day, month, year] = value.split("-").map(Number);
  return new Date(year, month - 1, day).getTime();
};

const getSortValue = (record: LandRecord, key: keyof LandRecord): string | number => {
  if (key === "Price Paid") return Number(record[key]) || 0;
  if (key === "Date Proprietor Added") return parseUkDate(record[key]);
  return (record[key] ?? "").toString().toLowerCase();
};

export const LandTable = (props: { companyNo: string | undefined }) => {
  const { companyNo } = props;
  const [records, setRecords] = useState<LandRecord[]>([]);
  const [filter, setFilter] = useState("");
  const [orderBy, setOrderBy] = useState<keyof LandRecord>("Property Address");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (companyNo == undefined) {
        return;
      }
      setLoading(true);
      const doc = await getLand(companyNo);
      setRecords(doc?.items ?? []);
      setLoading(false);
    };

    fetchData();
  }, [companyNo]);

  const filtered = useMemo(() => {
    let result = records;

    if (filter) {
      const lower = filter.toLowerCase();
      result = result.filter(
        (r) =>
          r["Property Address"]?.toLowerCase().includes(lower) ||
          r["Title Number"]?.toLowerCase().includes(lower) ||
          r["Postcode"]?.toLowerCase().includes(lower)
      );
    }

    return [...result].sort((a, b) => {
      const aVal = getSortValue(a, orderBy);
      const bVal = getSortValue(b, orderBy);
      if (aVal === bVal) return 0;
      const comparison = aVal > bVal ? 1 : -1;
      return order === "asc" ? comparison : -comparison;
    });
  }, [records, filter, orderBy, order]);

  const handleSort = (property: keyof LandRecord) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const visible = filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  if (loading) {
    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 200,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (records.length === 0) {
    return (
      <Box style={{ padding: 20 }}>
        <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
          No property titles recorded for this company.
        </Typography>
      </Box>
    );
  }

  return (
    <Box style={{ padding: "20px 10px 10px" }}>
      {records.length > 10 && (
        <Box style={{ padding: "0 10px 10px" }}>
          <TextField
            label="Filter by address, title number, or postcode"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(0);
            }}
            fullWidth
            size="small"
          />
        </Box>
      )}

      <Table>
        <TableHead>
          <TableRow>
            {(
              [
                ["Title Number", "Title Number"],
                ["Property Address", "Property Address"],
                ["Tenure", "Tenure"],
                ["Price Paid", "Price Paid"],
                ["Date Proprietor Added", "Date Registered"],
              ] as [keyof LandRecord, string][]
            ).map(([key, label]) => (
              <TableCell key={key}>
                <TableSortLabel
                  active={orderBy === key}
                  direction={orderBy === key ? order : "asc"}
                  onClick={() => handleSort(key)}
                >
                  {label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((r, index) => (
            <TableRow key={r["Title Number"] + index}>
              <TableCell>{getValueDisplay(r["Title Number"])}</TableCell>
              <TableCell>{getValueDisplay(r["Property Address"])}</TableCell>
              <TableCell>{getValueDisplay(r["Tenure"])}</TableCell>
              <TableCell>
                {r["Price Paid"] ? `£${Number(r["Price Paid"]).toLocaleString()}` : "--"}
              </TableCell>
              <TableCell>{getUkDateDisplay(r["Date Proprietor Added"])}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={ROWS_PER_PAGE}
        rowsPerPageOptions={[ROWS_PER_PAGE]}
      />
    </Box>
  );
};

export default LandTable;
